import { useState } from "react"
import { FaCheck, FaTimes, FaUserTie, FaUser, FaUsers, FaInfoCircle, FaChevronDown, FaChevronUp } from "react-icons/fa"
import { ToolTip } from "./ToolTip"

export default function PermissionTable() {
  const [expandedRows, setExpandedRows] = useState({})

  const toggleRow = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }))
  }

  const permissions = [
    {
      id: "create-projects",
      feature: "Create & Manage Projects",
      admin: true,
      user: false,
      guest: false,
      notes: "",
    },
    {
      id: "invite-users",
      feature: "Invite Users",
      admin: true,
      user: false,
      guest: false,
      notes: "",
    },
    {
      id: "create-edit-forms",
      feature: "Create & Edit Forms",
      admin: true,
      user: true,
      guest: false,
      notes: "",
    },
    {
      id: "view-forms",
      feature: "View Forms",
      admin: true,
      user: { limited: true, details: "Only projects they are invited to" },
      guest: { limited: true, details: "Approval, After Control, SIA, Observations" },
      notes: "",
    },
    {
      id: "approval-forms",
      feature: "Approval Forms",
      admin: true,
      user: true,
      guest: false,
      notes: "",
    },
    {
      id: "material-lists",
      feature: "Material Lists",
      admin: true,
      user: { limited: true, details: "Limited" },
      guest: false,
      notes: "User can create but not transfer",
    },
    {
      id: "submit-observations",
      feature: "Submit Observations (RUH)",
      admin: true,
      user: { limited: true, details: "Limited" },
      guest: { limited: true, details: "Limited" },
      notes: "User & Guest can only write observations not see them afterwards",
    },
    {
      id: "manage-material-lists",
      feature: "Manage Material Lists",
      admin: true,
      user: true,
      guest: false,
      notes: "",
    },
    {
      id: "track-scaffolding",
      feature: "Track Scaffolding Log",
      admin: true,
      user: true,
      guest: { limited: true, details: "Limited" },
      notes: "Guest can only see Active scaffolds",
    },
    {
      id: "view-project-overview",
      feature: "View Project Overview",
      admin: true,
      user: { limited: true, details: "Own projects only" },
      guest: { limited: true, details: "Limited" },
      notes: "",
    },
    {
      id: "modify-pricing",
      feature: "Modify Pricing & Rent Forms",
      admin: true,
      user: false,
      guest: false,
      notes:
        "Can we implement Invite to pricing where the creater of the project can invite any user? Same as we previously had to project. (this should be the only way anyone can see the prices)",
    },
  ]

  return (
    <div className="w-full overflow-x-auto shadow-lg rounded-lg">
      <table className="min-w-full bg-white border border-gray-200">
        <thead>
          <tr className="bg-gray-100">
            <th colSpan={4} className="px-4 py-3 text-left text-lg font-semibold text-blue-800 border-b">
              User Access Levels in Salus Scaffold
            </th>
          </tr>
          <tr className="bg-gray-50">
            <th className="px-4 py-3 text-left font-medium text-gray-700 border-b border-r">Feature</th>
            <th className="px-4 py-3 text-center font-medium text-gray-700 border-b border-r w-28">
              <div className="flex items-center justify-center gap-2">
                Admin <FaUserTie className="text-blue-700" />
              </div>
            </th>
            <th className="px-4 py-3 text-center font-medium text-gray-700 border-b border-r w-28">
              <div className="flex items-center justify-center gap-2">
                User <FaUser className="text-amber-500" />
              </div>
            </th>
            <th className="px-4 py-3 text-center font-medium text-gray-700 border-b w-28">
              <div className="flex items-center justify-center gap-2">
                Guest <FaUsers className="text-purple-600" />
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {permissions.map((permission, index) => (
            <tr
              key={permission.id}
              className={`${index % 2 === 0 ? "bg-white" : "bg-gray-50"} hover:bg-blue-50 transition-colors`}
            >
              <td className="px-4 py-3 border-b border-r">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-blue-700">{permission.feature}</span>
                  {permission.notes && (
                    <button onClick={() => toggleRow(permission.id)} className="text-gray-500 hover:text-blue-600">
                      {expandedRows[permission.id] ? <FaChevronUp /> : <FaChevronDown />}
                    </button>
                  )}
                </div>
                {expandedRows[permission.id] && permission.notes && (
                  <div className="mt-2 text-sm text-gray-600 bg-blue-50 p-2 rounded">{permission.notes}</div>
                )}
              </td>
              <td className="px-4 py-3 text-center border-b border-r">
                {permission.admin === true ? (
                  <FaCheck className="mx-auto text-green-500 text-lg" />
                ) : permission.admin === false ? (
                  <FaTimes className="mx-auto text-red-500 text-lg" />
                ) : (
                  <ToolTip content={permission.admin.details}>
                    <div className="flex items-center justify-center">
                      <span className="text-purple-600 font-medium">Limited</span>
                      <FaInfoCircle className="ml-1 text-gray-400" />
                    </div>
                  </ToolTip>
                )}
              </td>
              <td className="px-4 py-3 text-center border-b border-r">
                {permission.user === true ? (
                  <FaCheck className="mx-auto text-green-500 text-lg" />
                ) : permission.user === false ? (
                  <FaTimes className="mx-auto text-red-500 text-lg" />
                ) : (
                  <ToolTip content={permission.user.details}>
                    <div className="flex items-center justify-center">
                      <span className="text-purple-600 font-medium">Limited</span>
                      <FaInfoCircle className="ml-1 text-gray-400" />
                    </div>
                  </ToolTip>
                )}
              </td>
              <td className="px-4 py-3 text-center border-b">
                {permission.guest === true ? (
                  <FaCheck className="mx-auto text-green-500 text-lg" />
                ) : permission.guest === false ? (
                  <FaTimes className="mx-auto text-red-500 text-lg" />
                ) : (
                  <ToolTip content={permission.guest.details}>
                    <div className="flex items-center justify-center">
                      <span className="text-purple-600 font-medium">Limited</span>
                      <FaInfoCircle className="ml-1 text-gray-400" />
                    </div>
                  </ToolTip>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

